import { proxy } from "valtio";
import type {
  OverviewMetrics,
  ScreenMetrics,
  DeviceMetrics,
  GeoMetrics,
} from "../api/analytics-api";
import {
  getOverview,
  getScreens,
  getDevices,
  getGeography,
} from "../api/analytics-api";

/**
 * Per-section state to prevent cross-page re-renders.
 * Each section has its own loading/error state, so updating
 * screens.loading doesn't affect devices.loading subscribers.
 */
interface SectionState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

interface AnalyticsStore {
  overview: SectionState<OverviewMetrics>;
  screens: SectionState<ScreenMetrics>;
  devices: SectionState<DeviceMetrics>;
  geography: SectionState<GeoMetrics>;
  timeRange: number;
}

export const analyticsStore = proxy<AnalyticsStore>({
  overview: { data: null, loading: false, error: null },
  screens: { data: null, loading: false, error: null },
  devices: { data: null, loading: false, error: null },
  geography: { data: null, loading: false, error: null },
  timeRange: 30,
});

export function setTimeRange(days: number) {
  analyticsStore.timeRange = days;
}

export async function fetchOverview(days?: number) {
  const range = days ?? analyticsStore.timeRange;
  analyticsStore.overview.loading = true;
  analyticsStore.overview.error = null;
  try {
    analyticsStore.overview.data = await getOverview(range);
  } catch (e) {
    analyticsStore.overview.error =
      e instanceof Error ? e.message : "Failed to fetch overview";
  } finally {
    analyticsStore.overview.loading = false;
  }
}

export async function fetchScreens(days?: number) {
  const range = days ?? analyticsStore.timeRange;
  analyticsStore.screens.loading = true;
  analyticsStore.screens.error = null;
  try {
    analyticsStore.screens.data = await getScreens(range);
  } catch (e) {
    analyticsStore.screens.error =
      e instanceof Error ? e.message : "Failed to fetch screens";
  } finally {
    analyticsStore.screens.loading = false;
  }
}

export async function fetchDevices(days?: number) {
  const range = days ?? analyticsStore.timeRange;
  analyticsStore.devices.loading = true;
  analyticsStore.devices.error = null;
  try {
    analyticsStore.devices.data = await getDevices(range);
  } catch (e) {
    analyticsStore.devices.error =
      e instanceof Error ? e.message : "Failed to fetch devices";
  } finally {
    analyticsStore.devices.loading = false;
  }
}

export async function fetchGeography(days?: number) {
  const range = days ?? analyticsStore.timeRange;
  analyticsStore.geography.loading = true;
  analyticsStore.geography.error = null;
  try {
    analyticsStore.geography.data = await getGeography(range);
  } catch (e) {
    analyticsStore.geography.error =
      e instanceof Error ? e.message : "Failed to fetch geography";
  } finally {
    analyticsStore.geography.loading = false;
  }
}
